import React, { useState } from 'react';
import { TrashIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { AdderSelector, calculateAdderTotal, DOOR_ADDERS } from './AdderSelector';
import type { AdderSelection } from './AdderSelector';
import { calculateBasePrice, getMultiplierLabel } from './pricingUtils';
import { GLASS_OPTIONS, type GlassType, TEMPERED_GLASS_COST } from './WindowRow';
export const DOOR_OPERATION_OPTIONS = ['XO', 'OX', 'XXXO', 'OXXO', 'XXO', 'OXX', 'OXXX'] as const;
export type DoorOperationType = (typeof DOOR_OPERATION_OPTIONS)[number];
export interface DoorData {
  id: string;
  qty: number;
  width: number;
  height: number;
  glass: GlassType;
  tempered: boolean;
  operations: DoorOperationType[];
  adders: AdderSelection;
  included: boolean;
  notes: string;
}
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}
function DoorOperationSelector({
  operations,
  onChange,
  index
}: {
  operations: DoorOperationType[];
  onChange: (ops: DoorOperationType[]) => void;
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  function handleToggle(op: DoorOperationType) {
    if (operations.includes(op)) {
      onChange(operations.filter(o => o !== op));
    } else {
      onChange([...operations, op]);
    }
  }
  const label = operations.length === 0 ? 'Select' : operations.length === 1 ? operations[0] : `${operations.length} selected`;
  return <div className="relative" data-id="element-30">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors text-gray-700 font-medium min-w-[120px]" data-id="element-31">
        <span className="truncate" data-id="element-32">{label}</span>
        {isOpen ? <ChevronUpIcon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" data-id="element-33" /> : <ChevronDownIcon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" data-id="element-34" />}
      </button>

      {operations.length > 0 && !isOpen && <div className="flex flex-wrap gap-1 mt-1.5" data-id="element-35">
          {operations.map(op => <span key={op} className="inline-block px-1.5 py-0.5 text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded" data-id="element-36">
              {op}
            </span>)}
        </div>}

      {isOpen && <div className="absolute z-20 top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg" data-id="element-37">
          <div className="px-3 py-2 border-b border-gray-100" data-id="element-38">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-39">
              Operation
            </span>
          </div>
          <div className="py-1" data-id="element-40">
            {DOOR_OPERATION_OPTIONS.map(op => <div key={op} className="px-3 py-1.5 hover:bg-gray-50" data-id="element-41">
                <label className="flex items-center gap-2.5 cursor-pointer" data-id="element-42">
                  <input type="checkbox" checked={operations.includes(op)} onChange={() => handleToggle(op)} className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-0" data-id="element-43" />
                  <span className="text-sm text-gray-800" data-id="element-44">{op}</span>
                </label>
              </div>)}
          </div>
          <div className="px-3 py-2 border-t border-gray-100" data-id="element-45">
            <button type="button" onClick={() => setIsOpen(false)} className="w-full text-center text-sm font-medium text-emerald-700 hover:text-emerald-800 py-1" data-id="element-46">
              Done
            </button>
          </div>
        </div>}
    </div>;
}
interface DoorRowProps {
  door: DoorData;
  index: number;
  productLine: string;
  exteriorColorCost: number;
  onUpdate: (id: string, updates: Partial<DoorData>) => void;
  onRemove: (id: string) => void;
  canRemove: boolean;
}
export function DoorRow({
  door: win,
  index,
  productLine,
  exteriorColorCost,
  onUpdate,
  onRemove,
  canRemove
}: DoorRowProps) {
  const sqIn = win.width * win.height;
  const sqFt = sqIn / 144;
  const basePrice = calculateBasePrice(win.width, win.height, productLine);
  const adderCost = calculateAdderTotal(win.adders) + exteriorColorCost;
  const unitPrice = basePrice + adderCost;
  const lineTotal = unitPrice * win.qty;
  const isEven = index % 2 === 0;
  const multiplierLabel = getMultiplierLabel(sqIn, productLine);
  const dimmed = !win.included;
  return <tr className={`${isEven ? 'bg-white' : 'bg-gray-50/70'} ${dimmed ? 'opacity-40' : ''}`} data-id="element-47">
      <td className="px-2 py-3 text-center" data-id="element-48">
        <input type="checkbox" checked={win.included} onChange={() => onUpdate(win.id, {
        included: !win.included
      })} aria-label={`Include door ${index + 1} in quote`} className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-0" data-id="element-49" />
      </td>
      <td className="px-4 py-3 text-sm font-medium text-gray-600 text-center" data-id="element-50">
        {index + 1}
      </td>
      <td className="px-3 py-3" data-id="element-51">
        <input type="number" min={1} value={win.qty || ''} onChange={e => onUpdate(win.id, {
        qty: Math.max(1, Number(e.target.value))
      })} placeholder="1" aria-label={`Door ${index + 1} quantity`} className="w-16 px-2.5 py-1.5 text-sm text-right border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 tabular-nums bg-white" data-id="element-52" />
      </td>
      <td className="px-3 py-3" data-id="element-53">
        <input type="number" min={0} value={win.width || ''} onChange={e => onUpdate(win.id, {
        width: Number(e.target.value)
      })} placeholder="0" aria-label={`Door ${index + 1} width in inches`} className="w-20 px-2.5 py-1.5 text-sm text-right border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 tabular-nums bg-white" data-id="element-54" />
      </td>
      <td className="px-3 py-3" data-id="element-55">
        <input type="number" min={0} value={win.height || ''} onChange={e => onUpdate(win.id, {
        height: Number(e.target.value)
      })} placeholder="0" aria-label={`Door ${index + 1} height in inches`} className="w-20 px-2.5 py-1.5 text-sm text-right border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 tabular-nums bg-white" data-id="element-56" />
      </td>
      <td className="px-4 py-3 text-right" data-id="element-57">
        <span className="text-sm tabular-nums text-gray-700" data-id="element-58">
          {sqFt > 0 ? sqFt.toFixed(1) : '—'}
        </span>
        {multiplierLabel && <span className="block text-[11px] text-gray-400 tabular-nums" data-id="element-59">
            {multiplierLabel}
          </span>}
      </td>
      <td className="px-3 py-3" data-id="element-60">
        <DoorOperationSelector operations={win.operations} onChange={ops => onUpdate(win.id, {
        operations: ops
      })} index={index} data-id="element-61" />
      </td>
      <td className="px-3 py-3" data-id="element-62">
        <select value={win.glass} onChange={e => onUpdate(win.id, {
        glass: e.target.value as GlassType
      })} aria-label={`Door ${index + 1} glass obscurity`} className="px-2.5 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 min-w-[110px]" data-id="element-63">
          {GLASS_OPTIONS.map(opt => <option key={opt} value={opt} data-id="element-64">
              {opt}
            </option>)}
        </select>
      </td>
      <td className="px-3 py-3" data-id="element-65">
        <AdderSelector adders={win.adders} adderConfig={DOOR_ADDERS} onChange={adders => onUpdate(win.id, {
        adders
      })} data-id="element-66" />
      </td>
      <td className="px-3 py-3" data-id="element-67">
        <input type="text" value={win.notes} onChange={e => onUpdate(win.id, {
        notes: e.target.value
      })} placeholder="Room / notes" aria-label={`Door ${index + 1} notes`} className="w-28 px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 bg-white" data-id="element-68" />
      </td>
      <td className="px-3 py-3 text-center" data-id="element-69">
        {canRemove && <button type="button" onClick={() => onRemove(win.id)} aria-label={`Remove door ${index + 1}`} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors" data-id="element-70">
            <TrashIcon className="w-4 h-4" data-id="element-71" />
          </button>}
      </td>
    </tr>;
}